import React, { useState } from 'react'
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
  Typography,
  Grid,
} from '@mui/material'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import { LoadingButton } from '@mui/lab'
// import GoogleCalendarIcon from '../../../assets/icons/google_calendar.ico'
import GoogleMeetIcon from '../../../../../../assets/icons/google_meet.ico'
import { EventWithAdditionalInfo } from '../../../../../../redux/event/teamMemberEvents/types'
import { useAppSelector } from '../../../../../../redux/store'
import useUpdateTeamMemberEvent from '../../../../../../hooks/eventHooks/useUpdateTeamMemberEvent'
import EventBadges from '../../../../EventBadges'
import { setDateFormat, setTimeFormat } from '../../../../../../utils/dateUtils'
import { fontLineHeights, fontSizes } from '../../../../../Common/Typography/TypographyStyles'
import { brandColors, neutralColors } from '../../../../../Common/Color/Color'
import useConfirmationAlert from '../../../../../../hooks/componentHooks/useConfirmationAlert'
import { selectUserTeamMemberInfo } from '../../../../../../redux/auth/selector'
import { selectTeamMemberInfo } from '../../../../../../redux/teamMember/selector'

interface GeneralInformationTabPanelProps {
  eventDetail: EventWithAdditionalInfo
  onClose?: () => void
}

const GeneralInformationTabPanel: React.FC<GeneralInformationTabPanelProps> = ({
  eventDetail,
  onClose,
}) => {
  const [buttonLoading, setButtonLoading] = useState(false)

  const teamMemberDetails = useAppSelector(selectUserTeamMemberInfo)
  const { ConfirmationAlert, openConfirmationAlert } = useConfirmationAlert()
  const { handleUpdateEvent } = useUpdateTeamMemberEvent()
  const teamMemberInfo = useAppSelector(selectTeamMemberInfo)

  const onUpdateEvent = async () => {
    setButtonLoading(true)
    openConfirmationAlert({
      title: 'Confirm Registration',
      text: 'Are you sure you want to register for this event?',
      confirmButtonText: 'Yes, Register',
      cancelButtonText: 'Cancel',
      onConfirm: async () => {
        await handleUpdateEvent({
          teamMemberEmail: teamMemberDetails.email!,
          teamMemberWorkdayId: teamMemberDetails.workdayId!,
          eventId: eventDetail.eventDetails.id!,
          status: 'registered',
        })
        onClose?.()
        setButtonLoading(false)
      },
      onCancel: () => {
        setButtonLoading(false)
      },
    })
  }

  return (
    <Grid container spacing={2} direction='column'>
      <Grid>
        <Box>
          <Stack direction='row' spacing={2}>
            <EventBadges value={eventDetail.eventDetails.category || ''} badgeType='categoryBadge' />
            <EventBadges value={eventDetail.eventDetails || ''} badgeType='pointsBadge' />
          </Stack>
        </Box>
      </Grid>
      <Grid>
        <Box>
          <Stack direction='row' spacing={2}>
            {eventDetail && eventDetail.additionalInfo.status && (
              <LoadingButton
                loading={buttonLoading}
                variant='contained'
                disabled={eventDetail.additionalInfo.status === 'registered'}
                onClick={onUpdateEvent}
              >
                {eventDetail.additionalInfo.status === 'registered' ? 'Registered' : 'Register'}
              </LoadingButton>
            )}
            {eventDetail.eventDetails.postSurveyURL && eventDetail.eventDetails.isCompleted && (
              <Button
                variant='contained'
                href={eventDetail.eventDetails.postSurveyURL}
                disabled={eventDetail.additionalInfo.isSurveyDone}
                target='_blank'
                rel='noopener noreferrer'
              >
                {eventDetail.additionalInfo.isSurveyDone ? 'Survey Completed' : 'Take a Survey'}
              </Button>
            )}
            {eventDetail.eventDetails.googleMeetLink && !eventDetail.eventDetails.isCompleted && (
              <IconButton
                href={eventDetail.eventDetails.googleMeetLink}
                target='_blank'
                rel='noopener noreferrer'
              >
                <img src={GoogleMeetIcon} alt='Google Meet' style={{ width: '24px', height: '24px' }} />
              </IconButton>
            )}
          </Stack>
        </Box>
      </Grid>

      <Grid container spacing={2} direction='column'>
        <Box
          component='div'
          sx={{
            overflow: 'auto',
            width: 'auto',
            height: 100,
            backgroundColor: neutralColors.greyMystic,
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: brandColors.purpleTelus,
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: '#555',
            },
            scrollbarWidth: 'auto',
            msOverflowStyle: 'auto',
          }}
          p={1}
          borderRadius={2}
        >
          <Typography
            sx={{
              fontSize: fontSizes.size14,
              lineHeight: fontLineHeights.ratio9to4,
            }}
          >
            {eventDetail.eventDetails.details}
          </Typography>
        </Box>
        <Box display='flex' alignItems='center'>
          <FormControl variant='outlined' fullWidth>
            <InputLabel htmlFor='venue-input-field'>EVENT VENUE</InputLabel>
            <OutlinedInput
              id='venue-input-field'
              type='text'
              value={eventDetail.eventDetails.venue}
              defaultValue={eventDetail.eventDetails.venue}
              readOnly
              label='EVENT VENUE'
              startAdornment={
                <InputAdornment position='start'>
                  <LocationOnIcon />
                </InputAdornment>
              }
            />
          </FormControl>
        </Box>
      </Grid>
      <Grid container justifyContent='flex-end'>
        <Box display='flex' flexDirection='column' alignItems='flex-end' sx={{ padding: 2 }}>
          <Typography variant='body1' fontWeight='bold'>
            Created By
          </Typography>
          <Typography variant='body2' color='textSecondary'>
            {teamMemberInfo?.firstName} {teamMemberInfo?.lastName} {teamMemberInfo?.suffix}
          </Typography>
          <Typography variant='body2' color='textSecondary'>
            {`${setDateFormat(eventDetail.eventDetails.createdAt)} ${setTimeFormat(eventDetail.eventDetails.createdAt)}`}
          </Typography>
        </Box>
      </Grid>
      {ConfirmationAlert}
    </Grid>
  )
}

export default GeneralInformationTabPanel
